import { type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { AlertCircle, Check, ClipboardCheck, Eye, Pencil, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import type { ApiError } from "@/lib/api/unwrap";
import { cn } from "@/lib/utils/cn";

import { useDashboardQuery } from "../api/hooks";
import type { DashboardEnjeu, DashboardProfile, ExpertStatus } from "../types";
import { ScopeFacets } from "./ui/ScopeFacets";
import { EmptyState, ErrorState, Loading } from "./ui/states";

function isApiError(err: unknown): err is ApiError {
  return typeof err === "object" && err !== null && "status" in err;
}

const PILL: Record<ExpertStatus, string> = {
  draft: "bg-secondary text-secondary-foreground",
  submitted: "bg-warning/10 text-warning",
  validated: "bg-success/10 text-success",
  rejected: "bg-primary/10 text-primary",
};

function monogram(p: DashboardProfile): string {
  const first = (p.contact_primary_first_name || p.organization_name || "").trim();
  const last = (p.contact_primary_last_name || "").trim();
  return ((first[0] ?? "") + (last[0] ?? "")).toUpperCase() || "?";
}

export function DashboardView({ onGoRegister }: { onGoRegister: () => void }) {
  const { t } = useTranslation();
  const dashboard = useDashboardQuery();

  if (dashboard.isPending) return <Loading />;

  // 404 = no finalized expert profile — point the user at registration.
  if (dashboard.isError) {
    const notFound = isApiError(dashboard.error) && dashboard.error.status === 404;
    if (notFound) {
      return (
        <EmptyState title={t("expert.dashboard.notFound")}>
          <Button onClick={onGoRegister}>{t("expert.dashboard.goToRegister")}</Button>
        </EmptyState>
      );
    }
    return <ErrorState onRetry={() => void dashboard.refetch()} />;
  }

  const { profile, stats } = dashboard.data;
  const status = profile.status as ExpertStatus;
  // Approved but Part 2 (objectives + actions) not done yet.
  const partTwoPending = status === "validated" && profile.part_two_pending;
  // Part 2, or an enjeu the CQDD just approved with nothing chosen under it.
  const objectivesPending = status === "validated" && profile.objectives_pending;
  const pending = status === "validated" && profile.has_pending_changes;
  const leadKey =
    status === "validated"
      ? partTwoPending
        ? "approved"
        : pending
          ? "validatedPending"
          : "validated"
      : status; // submitted | rejected | draft

  return (
    <div className="space-y-6">
      {objectivesPending && (
        // Objectives waiting to be chosen: a red band that stays in view while
        // the dashboard scrolls (the app header scrolls away, so it pins to
        // the top of the viewport).
        <div
          role="status"
          className="sticky top-0 z-10 flex flex-wrap items-center gap-4 rounded-lg bg-destructive px-5 py-3.5 text-destructive-foreground shadow-lg md:flex-nowrap"
        >
          <TriangleAlert className="size-6 flex-none" aria-hidden />
          <div className="min-w-0 flex-1 basis-[calc(100%-2.5rem)] text-sm leading-relaxed md:basis-auto">
            <p className="text-[15px] font-bold">{t("expert.dashboard.lastStep.title")}</p>
            <p>
              {t(
                partTwoPending
                  ? "expert.dashboard.lastStep.body"
                  : "expert.dashboard.lastStep.newIssueBody",
              )}
            </p>
          </div>
          <Link
            to="/experts/objectives"
            className="inline-flex min-h-11 w-full flex-none items-center justify-center gap-2 rounded-md bg-destructive-foreground px-4 text-sm font-semibold text-destructive hover:bg-destructive-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-destructive md:w-auto"
          >
            <Check className="size-4" aria-hidden />
            {t("expert.dashboard.completeProfile")}
          </Link>
        </div>
      )}

      {/* ---- hero: the private twin of the public listing ---- */}
      <div className="flex flex-wrap items-start gap-5 rounded-lg border border-border bg-card p-6">
        <div
          aria-hidden
          className="grid size-16 flex-none place-items-center rounded-2xl border border-primary/20 bg-primary/10 text-2xl font-bold uppercase text-primary"
        >
          {monogram(profile)}
        </div>
        <div className="min-w-0 flex-1">
          <span
            className={cn(
              "mb-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide",
              PILL[status],
            )}
          >
            <span className="size-2 rounded-full bg-current" aria-hidden />
            {t(`expert.dashboard.status.${status}`)}
          </span>
          <h2 className="text-2xl font-bold tracking-tight">
            {profile.organization_name || t("expert.dashboard.heading")}
          </h2>
          {leadKey !== "draft" && (
            <p className="mt-1.5 max-w-prose text-sm text-muted-foreground">
              {t(`expert.dashboard.lead.${leadKey}`)}
            </p>
          )}
        </div>
        {status !== "draft" && (
          <div className="flex flex-none flex-wrap gap-2">
            {partTwoPending ? null : status === "validated" ? (
              <>
                <Button variant="outline" asChild>
                  <Link
                    to="/experts/profile/$id"
                    params={{ id: profile.id }}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Eye className="mr-1.5 size-4" aria-hidden />
                    {t("expert.dashboard.viewPublic")}
                  </Link>
                </Button>
                {/* Part 2 is done — allow editing the objectives/actions
                    (staged for CQDD re-validation server-side). */}
                <Button variant="outline" asChild>
                  <Link to="/experts/objectives">
                    <ClipboardCheck className="mr-1.5 size-4" aria-hidden />
                    {t("expert.dashboard.editObjectives")}
                  </Link>
                </Button>
              </>
            ) : null}
            <Button variant="outline" onClick={onGoRegister}>
              <Pencil className="mr-1.5 size-4" aria-hidden />
              {status === "rejected"
                ? t("expert.dashboard.editResubmit")
                : t("expert.dashboard.edit")}
            </Button>
          </div>
        )}
      </div>

      {/* ---- status banners ---- */}
      {status === "rejected" && profile.review_message && (
        <Note tone="rejected" label={t("expert.dashboard.banner.rejectedLabel")}>
          {profile.review_message}
        </Note>
      )}
      {pending && (
        <Note tone="pending" label={t("expert.dashboard.banner.pendingLabel")}>
          {t("expert.dashboard.banner.pendingBody")}
        </Note>
      )}

      {/* ---- at-a-glance stats ---- */}
      <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-border sm:grid-cols-4">
        <Stat label={t("expert.dashboard.stats.enjeux")} value={stats.enjeux} />
        <Stat label={t("expert.dashboard.stats.actions")} value={stats.actions} />
        <Stat label={t("expert.dashboard.stats.regions")} value={stats.regions} />
        <Stat label={t("expert.dashboard.stats.mandates")} value={stats.mandates} last />
      </div>

      {/* ---- full profile detail ---- */}
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
        {t("expert.dashboard.profileEyebrow")}
      </p>
      <IdentityCard profile={profile} />
      <OrgCard profile={profile} />
      <ScopeCard profile={profile} />
      <ExpertisesCard profile={profile} />
    </div>
  );
}

function Note({
  tone,
  label,
  children,
}: {
  tone: "pending" | "rejected";
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border p-3.5 text-sm",
        tone === "pending"
          ? "border-warning/30 bg-warning/10"
          : "border-primary/25 bg-primary/[0.06]",
      )}
    >
      <AlertCircle
        className={cn(
          "mt-0.5 size-[18px] flex-none",
          tone === "pending" ? "text-warning" : "text-primary",
        )}
        aria-hidden
      />
      <div>
        <p
          className={cn(
            "text-xs font-bold uppercase tracking-wide",
            tone === "pending" ? "text-warning" : "text-primary",
          )}
        >
          {label}
        </p>
        <p className="mt-0.5 whitespace-pre-line">{children}</p>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  last,
}: {
  label: string;
  value: number;
  last?: boolean;
}) {
  return (
    <div className={cn("bg-card px-4 py-4 text-center", !last && "border-r border-border")}>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function DetailCard({
  title,
  extra,
  children,
}: {
  title: string;
  extra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-border">
      <div className="flex items-center justify-between gap-2 border-b border-border bg-secondary/40 px-4 py-3">
        <h3 className="text-sm font-bold">{title}</h3>
        {extra}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

/** A dl row; renders "—" when the value is empty. */
function Row({ label, children }: { label: string; children?: ReactNode }) {
  const { t } = useTranslation();
  const empty =
    children == null || children === "" || (Array.isArray(children) && !children.length);
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="m-0">{empty ? t("expert.dashboard.detail.none") : children}</dd>
    </>
  );
}

function ContactBlock({
  heading,
  first,
  last,
  fn,
  email,
  phone,
  ext,
  linkedin,
}: {
  heading: string;
  first: string;
  last: string;
  fn: string;
  email: string;
  phone: string;
  ext: string;
  linkedin: string;
}) {
  const { t } = useTranslation();
  const hasName = Boolean(first || last);
  return (
    <div>
      <h6 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
        {heading}
      </h6>
      {hasName || email || phone ? (
        <p className="mt-1 text-sm leading-relaxed">
          {hasName && (
            <span className="font-medium">
              {[first, last].filter(Boolean).join(" ")}
            </span>
          )}
          {fn && <span className="text-muted-foreground"> — {fn}</span>}
          {email && (
            <>
              <br />
              <a className="text-theme-economic-ink hover:underline" href={`mailto:${email}`}>
                {email}
              </a>
            </>
          )}
          {phone && (
            <>
              <br />
              {phone}
              {ext && ` #${ext}`}
            </>
          )}
          {linkedin && (
            <>
              <br />
              <a
                className="text-theme-economic-ink hover:underline"
                href={linkedin}
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
            </>
          )}
        </p>
      ) : (
        <p className="mt-1 text-sm text-muted-foreground">
          {t("expert.dashboard.detail.none")}
        </p>
      )}
    </div>
  );
}

function IdentityCard({ profile: p }: { profile: DashboardProfile }) {
  const { t } = useTranslation();
  const d = (k: string) => t(`expert.dashboard.detail.${k}`);
  return (
    <DetailCard title={d("identity")}>
      <div className="grid gap-6 md:grid-cols-2">
        <dl className="grid grid-cols-[max-content_1fr] items-baseline gap-x-4 gap-y-2 text-sm">
          <Row label={d("legalName")}>{p.legal_name_req}</Row>
          <Row label={d("neq")}>{p.neq}</Row>
          <Row label={d("email")}>
            {p.email ? (
              <a className="text-theme-economic-ink hover:underline" href={`mailto:${p.email}`}>
                {p.email}
              </a>
            ) : null}
          </Row>
          <Row label={d("phone")}>{p.phone}</Row>
          <Row label={d("website")}>
            {p.website ? (
              <a
                className="text-theme-economic-ink hover:underline"
                href={p.website}
                target="_blank"
                rel="noopener noreferrer"
              >
                {p.website}
              </a>
            ) : null}
          </Row>
          <Row label={d("headOffice")}>
            {p.head_office_address || p.head_office_region ? (
              <>
                {p.head_office_address || "—"}
                {p.head_office_region && (
                  <span className="block text-xs text-muted-foreground">
                    {p.head_office_region.name}
                    {p.head_office_mrc && ` — ${p.head_office_mrc}`}
                  </span>
                )}
              </>
            ) : null}
          </Row>
          <Row label={d("otherLocations")}>
            {p.business_locations.length > 0
              ? p.business_locations.map((place, i) => (
                  <span key={i} className="block">
                    {place.address ? (
                      <>
                        {place.address}
                        <span className="block text-xs text-muted-foreground">
                          {place.region} — {place.mrc}
                        </span>
                      </>
                    ) : (
                      `${place.region} — ${place.mrc}`
                    )}
                  </span>
                ))
              : null}
          </Row>
          <Row label={d("otherProvinces")}>
            {p.serves_other_provinces == null
              ? null
              : p.serves_other_provinces
                ? p.other_province_names.join(", ")
                : t("expert.register.no")}
          </Row>
        </dl>
        <div className="grid gap-4 sm:grid-cols-2">
          <ContactBlock
            heading={d("primaryContact")}
            first={p.contact_primary_first_name}
            last={p.contact_primary_last_name}
            fn={p.contact_primary_function}
            email={p.contact_primary_email}
            phone={p.contact_primary_phone}
            ext={p.contact_primary_extension ?? ""}
            linkedin={p.contact_primary_linkedin}
          />
          <ContactBlock
            heading={d("secondaryContact")}
            first={p.contact_secondary_first_name}
            last={p.contact_secondary_last_name}
            fn={p.contact_secondary_function}
            email={p.contact_secondary_email}
            phone={p.contact_secondary_phone}
            ext={p.contact_secondary_extension}
            linkedin={p.contact_secondary_linkedin}
          />
        </div>
      </div>
    </DetailCard>
  );
}

function OrgCard({ profile: p }: { profile: DashboardProfile }) {
  const { t } = useTranslation();
  const d = (k: string) => t(`expert.dashboard.detail.${k}`);
  const rows: [string, ReactNode][] = [];
  if (p.founding_year) rows.push([d("foundingYear"), p.founding_year]);
  if (p.dd_services_start_year) rows.push([d("ddStart"), p.dd_services_start_year]);
  if (p.total_employees) rows.push([d("employees"), p.total_employees]);
  if (p.dedicated_experts_count) rows.push([d("dedicated"), p.dedicated_experts_count]);
  if (p.awards) rows.push([d("awards"), p.awards]);
  if (p.affiliations) rows.push([d("affiliations"), p.affiliations]);

  if (rows.length === 0 && !p.description) return null;
  return (
    <DetailCard title={d("org")}>
      {/* description first, then the identity rows. */}
      {p.description && (
        <p className="mb-3 whitespace-pre-line text-sm leading-relaxed">
          {p.description}
        </p>
      )}
      {rows.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] items-baseline gap-x-4 gap-y-2 text-sm sm:grid-cols-[max-content_1fr_max-content_1fr]">
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="m-0">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </DetailCard>
  );
}

function ScopeCard({ profile: p }: { profile: DashboardProfile }) {
  const { t } = useTranslation();
  const d = (k: string) => t(`expert.dashboard.detail.${k}`);
  // chosen options green, not-chosen red, all-selected collapses.
  return (
    <DetailCard title={d("scope")}>
      <ScopeFacets facets={p.scope_facets} />
    </DetailCard>
  );
}

function EnjeuBlock({ enjeu }: { enjeu: DashboardEnjeu }) {
  const { t } = useTranslation();
  const d = (k: string) => t(`expert.dashboard.detail.${k}`);
  const m = enjeu.mandate;
  return (
    <div className="border-t border-border p-4 first:border-t-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="font-bold">{enjeu.issue.name}</h4>
        <span className="inline-flex flex-wrap gap-1.5">
          {enjeu.preapproved && (
            <span className="rounded-full bg-success/10 px-2.5 py-0.5 text-xs font-semibold text-success">
              {d("preapproved")}
            </span>
          )}
          {enjeu.mandate_count != null && (
            <span className="rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground">
              {t("expert.dashboard.detail.mandateCount", {
                count: enjeu.mandate_count,
              })}
            </span>
          )}
        </span>
      </div>

      {enjeu.objectives.length > 0 && (
        <>
          <p className="mt-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {d("objectives")}
          </p>
          <div className="mt-1.5 space-y-2">
            {enjeu.objectives.map((obj) => (
              <div key={obj.goal.id}>
                <p className="text-sm font-medium">{obj.goal.name}</p>
                {obj.actions.length > 0 ? (
                  <ul className="ml-4 list-disc text-sm text-muted-foreground">
                    {obj.actions.map((a) => (
                      <li key={a.id}>{a.name}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="ml-1 text-sm text-muted-foreground">{d("noActions")}</p>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {m && (
        <>
          <p className="mt-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">
            {d("mandate")}
          </p>
          <div className="mt-1.5 rounded-md border border-border bg-secondary/30 p-3">
            <strong className="text-sm">{m.name}</strong>
            {m.description && (
              <p className="mt-1.5 whitespace-pre-line text-sm">{m.description}</p>
            )}
            {m.clients.length > 0 && (
              <>
                <p className="mt-2.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  {d("clients")}
                </p>
                <ul className="ml-4 list-disc text-sm">
                  {m.clients.map((c, i) => (
                    <li key={i}>
                      <span className="font-medium">{c.org_name || d("noName")}</span>
                      {/* The CQDD may contact any client (the block says so): no
                          permission flag, the details are always listed. */}
                      <span className="text-muted-foreground">
                        {(c.contact_name || c.email || c.phone) && " — "}
                        {c.contact_name}
                        {c.contact_name && (c.email || c.phone) && " · "}
                        {c.email && (
                          <a className="text-theme-economic-ink hover:underline" href={`mailto:${c.email}`}>
                            {c.email}
                          </a>
                        )}
                        {c.email && c.phone && " · "}
                        {c.phone}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ExpertisesCard({ profile: p }: { profile: DashboardProfile }) {
  const { t } = useTranslation();
  const d = (k: string) => t(`expert.dashboard.detail.${k}`);
  return (
    <DetailCard
      title={d("expertises")}
      extra={
        <span className="rounded-full border border-border bg-card px-2.5 py-0.5 text-xs text-muted-foreground">
          {t("expert.dashboard.detail.enjeuxCount", { count: p.enjeux.length })}
        </span>
      }
    >
      {p.enjeux.length === 0 ? (
        <p className="text-sm text-muted-foreground">{d("noExpertise")}</p>
      ) : (
        <div className="-m-4">
          {p.enjeux.map((enjeu) => (
            <EnjeuBlock key={enjeu.issue.id} enjeu={enjeu} />
          ))}
        </div>
      )}
    </DetailCard>
  );
}
